import React, { useState, useEffect, useRef } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    Button,
    FormGroup,
    Label,
    Input,
    Row,
    Col,
    Alert,
    UncontrolledTooltip,
} from 'reactstrap';
import { Icon } from '@iconify/react';
import Select from 'react-select';
import { AvForm, AvField } from 'availity-reactstrap-validation';
import { toast } from 'react-toastify';

export const ActionButton = ({ icon, handleOnClick, title, index, action, disabled }) => {
    return (
        <div
            className={`d-flex justify-content-center align-items-center ${!disabled && 'action-button'}`}
            style={{ height: '45px', width: '45px' }}
            onClick={() => {
                !disabled && handleOnClick(action);
            }}
            id={'tooltip' + index}>
            <UncontrolledTooltip placement="top" target={'tooltip' + index}>
                {title}
            </UncontrolledTooltip>
            {React.isValidElement(icon) ? icon : <Icon icon={icon} width="25" />}
        </div>
    );
};

export const ActionCommentModal = ({ isOpen, toggle, action, color, label, submitAction, loading, widget, warning, requireUsername = false }) => {
    const [comment, setComment] = useState('');
    const [username, setUsername] = useState('');
    const showUsername = requireUsername || action?.toLowerCase() == 'limitedapproval';

    function preToggle() {
        setComment('');
        setUsername('');
        toggle();
    }

    useEffect(() => {
        setComment('');
        setUsername('');
    }, [isOpen]);

    function validateAndSubmit() {
        if (showUsername) {
            if (username.trim() == '') {
                toast.error('Please enter user name');
                return;
            }
            submitAction(action, comment, widget, undefined, username.trim());
        } else {
            submitAction(action, comment, widget);
        }
    }
    const textAreaRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => {
                if (textAreaRef.current) {
                    textAreaRef.current.focus();
                }
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    return (
        <Modal size="sm" style={{ marginTop: '100px' }} isOpen={isOpen} toggle={preToggle}>
            <ModalHeader
                className="gradient-modal"
                toggle={preToggle}
                style={{
                    background: 'linear-gradient(90deg, #452f69 0%, #452f69 50%, #000 90%)',
                    color: 'white',
                }}>
                {label}
            </ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label>Add comment: <small>not required</small></Label>
                    <Input
                        innerRef={textAreaRef}
                        type="textarea"
                        rows={2}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}></Input>
                </FormGroup>
                <FormGroup style={{ display: showUsername ? 'block' : 'none' }}>
                    <Label>Please enter name*:</Label>
                    <Input
                        type="text"
                        value={username}
                        required
                        onChange={(e) => setUsername(e.target.value)}></Input>
                </FormGroup>
                <FormGroup className="d-flex justify-content-end mb-0">
                    <Button color={color} disabled={loading} onClick={() => validateAndSubmit()}>
                        {label}
                    </Button>
                </FormGroup>
                {warning && (
                    <div className="mt-2">
                        <Alert color="danger"><small>{warning}</small></Alert>
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

export const ActionSendModal = ({ isOpen, toggle, action, color, label, submitAction, loading, widget }) => {
    const [selectedReason, setSelectedReason] = useState(null);
    const [comment, setComment] = useState('');

    function preToggle() {
        setValidState({ selectedReason: true });
        setSelectedReason(null);
        setComment('');
        toggle();
    }

    useEffect(() => {
        setSelectedReason(null);
        setComment('');
    }, [isOpen]);

    const [validState, setValidState] = useState({ selectedReason: true });
    const sendReasons = [
        { value: 'MD aware of interaction and approved', label: 'MD aware of interaction and approved' },
        { value: 'MD aware of allergy and approved', label: 'MD aware of allergy and approved' },
        { value: 'MD Okayed', label: 'MD Okayed' },
        { value: 'other', label: 'Other. See comment...' },
    ];

    function beforeSubmitValidation() {
        if (!selectedReason) {
            setValidState({ selectedReason: false });
            return false;
        }
        return true;
    }

    return (
        <Modal size="sm" style={{ marginTop: '100px' }} isOpen={isOpen} toggle={preToggle}>
            <ModalHeader className="gradient-modal" toggle={preToggle} style={{ background: 'linear-gradient(90deg, #452f69 0%, #452f69 50%, #000 90%)', color: 'white' }}>
                {label}
            </ModalHeader>
            <ModalBody>
                <AvForm onValidSubmit={() => { if (!beforeSubmitValidation()) return; submitAction(action, selectedReason === 'other' ? comment : selectedReason, widget); }}>
                    <Row>
                        <Col>
                            <FormGroup>
                                <Label for="pharmacy-role" style={{ color: !validState.selectedReason ? '#fa5c7c' : '' }}>Select Reason *</Label>
                                <Select
                                    className={!validState.selectedReason ? 'invalid-select' : 'react-select'}
                                    classNamePrefix="react-select"
                                    name="select-reason"
                                    placeholder="Select reason..."
                                    onChange={(selection) => { setValidState({ ...validState, selectedReason: true }); setSelectedReason(selection.value); }}
                                    value={sendReasons?.find((r) => r.value === selectedReason) || ''}
                                    isSearchable={false}
                                    options={sendReasons}></Select>
                                {!validState.selectedReason && <div className="invalid-text">Please select a reason</div>}
                            </FormGroup>
                        </Col>
                    </Row>
                    {selectedReason === 'other' && (
                        <AvField className="validation-textarea" type="textarea" name="comment" label="Enter Comment *" placeholder="Comment..." value={comment} onChange={(e) => setComment(e.target.value)} required />
                    )}
                    <FormGroup className="d-flex justify-content-end mb-0">
                        <Button color={color} disabled={loading}>{label}</Button>
                    </FormGroup>
                </AvForm>
            </ModalBody>
        </Modal>
    );
};

export const ActionDoNotSendModal = ({ isOpen, toggle, action, color, label, submitAction, loading, widget }) => {
    const [selectedReason, setSelectedReason] = useState(null);

    function preToggle() {
        setSelectedReason(null);
        setValidState({ selectedReason: true });
        toggle();
    }

    useEffect(() => { setSelectedReason(null); }, [isOpen]);

    const [validState, setValidState] = useState({ selectedReason: true });
    const sendReasons = [
        { value: 'new Rx of change order will be sent to pharmacy', label: 'new Rx of change order will be sent to pharmacy' },
        { value: 'd/c order will be sent to pharmacy', label: 'd/c order will be sent to pharmacy' },
    ];

    function beforeSubmitValidation() {
        if (!selectedReason) {
            setValidState({ selectedReason: false });
            return false;
        }
        return true;
    }

    return (
        <Modal size="sm" style={{ marginTop: '100px' }} isOpen={isOpen} toggle={preToggle}>
            <ModalHeader className="gradient-modal" toggle={preToggle} style={{ background: 'linear-gradient(90deg, #452f69 0%, #452f69 50%, #000 90%)', color: 'white' }}>
                {label}
            </ModalHeader>
            <ModalBody>
                <AvForm onValidSubmit={() => { if (!beforeSubmitValidation()) return; submitAction(action, selectedReason, widget); }}>
                    <Row>
                        <Col>
                            <FormGroup>
                                <Label for="pharmacy-role" style={{ color: !validState.selectedReason ? '#fa5c7c' : '' }}>Select Reason *</Label>
                                <Select
                                    className={!validState.selectedReason ? 'invalid-select' : 'react-select'}
                                    classNamePrefix="react-select"
                                    name="select-reason"
                                    placeholder="Select reason..."
                                    onChange={(selection) => { setValidState({ ...validState, selectedReason: true }); setSelectedReason(selection.value); }}
                                    value={sendReasons?.find((r) => r.value === selectedReason) || ''}
                                    isSearchable={false}
                                    options={sendReasons}></Select>
                                {!validState.selectedReason && <div className="invalid-text">Please select a reason</div>}
                            </FormGroup>
                        </Col>
                    </Row>
                    <FormGroup className="d-flex justify-content-end mb-0">
                        <Button color={color} disabled={loading}>{label}</Button>
                    </FormGroup>
                </AvForm>
            </ModalBody>
        </Modal>
    );
};

export const ActionQuestionModal = ({ isOpen, toggle, action, color, label, submitAction, loading, widget }) => {
    const [question, setQuestion] = useState('');

    function preToggle() {
        setQuestion('');
        toggle();
    }

    useEffect(() => { setQuestion(''); }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                const textarea = document.querySelector('.validation-textarea');
                if (textarea) textarea.focus();
            }, 500);
        }
    }, [isOpen]);

    return (
        <Modal size="sm" style={{ marginTop: '100px' }} isOpen={isOpen} toggle={preToggle}>
            <ModalHeader className="gradient-modal" toggle={preToggle} style={{ background: 'linear-gradient(90deg, #452f69 0%, #452f69 50%, #000 90%)', color: 'white' }}>
                {label}
            </ModalHeader>
            <ModalBody>
                <AvForm onValidSubmit={() => submitAction(action, question, widget)}>
                    <AvField className="validation-textarea" type="textarea" name="question" label="Enter Question *" placeholder="Question..." value={question} onChange={(e) => setQuestion(e.target.value)} required />
                    <FormGroup className="d-flex justify-content-end mb-0">
                        <Button color={color} disabled={loading}>{label}</Button>
                    </FormGroup>
                </AvForm>
            </ModalBody>
        </Modal>
    );
};

export const ActionLimitedApprovalModal = ({ isOpen, toggle, action, color, label, submitAction, loading, widget }) => {
    const [comment, setComment] = useState('');
    const [username, setUsername] = useState('');

    function preToggle() { toggle(); }

    useEffect(() => { setComment(''); setUsername(''); }, [isOpen]);

    function validateAndSubmit() {
        if (username.trim() == '') {
            toast.error('Please enter user name');
            return;
        }
        submitAction(action, comment, widget, selectedDays, username.trim());
    }

    const textAreaRef = useRef(null);
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => {
                const el = textAreaRef.current;
                if (el) el.focus();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const dayOptions = Array.from({ length: 30 }, (_, i) => i + 1);
    const [selectedDays, setSelectedDays] = useState(0);

    return (
        <Modal size="sm" style={{ marginTop: '100px' }} isOpen={isOpen} toggle={preToggle}>
            <ModalHeader className="gradient-modal" toggle={preToggle} style={{ background: 'linear-gradient(90deg, #452f69 0%, #452f69 50%, #000 90%)', color: 'white' }}>
                {label}
            </ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label>Add comment: <small>not required</small></Label>
                    <Input innerRef={textAreaRef} type="textarea" rows={2} value={comment} onChange={(e) => setComment(e.target.value)}></Input>
                </FormGroup>
                <FormGroup>
                    <Label>Please enter name*:</Label>
                    <Input type="text" value={username} required onChange={(e) => setUsername(e.target.value)}></Input>
                </FormGroup>
                <div className="d-flex justify-content-between">
                    <Select
                        placeholder="Select days"
                        styles={{ control: (base) => ({ ...base, width: '8rem' }) }}
                        options={dayOptions.map((option) => ({ value: option, label: option + ' Days' }))}
                        onChange={(selection) => setSelectedDays(selection.value)}></Select>
                    <Button color={color} onClick={validateAndSubmit}>Limit Days</Button>
                </div>
                <div className="mt-2">
                    <Alert color="danger"><small>If the limited day supply chosen is not available, the smallest available package size will be dispensed.</small></Alert>
                </div>
            </ModalBody>
        </Modal>
    );
};
